import {
  ChangeDetectionStrategy,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  Renderer2,
  SimpleChange,
  SimpleChanges,
  TrackByFunction
} from '@angular/core'
import { ITab, TabComponent } from './tab.component'
import { flatMap, map, startWith, tap } from 'rxjs/operators'
import { Observable, Subject } from 'rxjs'
import { maybe } from 'typescript-monads'
import { capitalizeFirstChar } from '../../../shared/utility-functions/string.utility'

/**
 * convert the query list into an array
 * filter - removes the streams tab if showStreamTile is false, else show the rest of the tabs
 *
 * @param ql QueryList of TabComponent
 * @param eventAnalytics object that contains node_id and live_id
 * @param showStreamTile boolean to show stream tile inside streams tab
 * @returns an array of tabs to be displayed
 */
function toTabArray(
  ql: QueryList<TabComponent>,
  eventAnalytics: any,
  showStreamTile: boolean
): ReadonlyArray<TabComponent> {
  return ql
    .toArray()
    .filter(a => a.title !== 'streams' || (showStreamTile && a.title === 'streams'))
    .map(a => {
      return {
        analyticProps: {
          name: a.title ? capitalizeFirstChar(a.title) : 'tab',
          ...eventAnalytics
        },
        ...a,
        title: a.title ? a.title : 'tab'
      }
    })
}

function maybeMapByExtractingCurrentValue(initialTab: SimpleChange) {
  return maybe<string>(initialTab.currentValue)
}

function updateTabSource(sub: Subject<string>) {
  return (value: string) => sub.next(value)
}

function applyStyles(tabTitle: string, tabs: ReadonlyArray<TabComponent>) {
  return tabs.forEach((val, index) => {
    const el = val.elementRef.nativeElement
    if (val.title === tabTitle || (tabTitle === 'firstTab' && index === 0)) {
      val.isActive = true
      el.style.display = 'block'
    } else {
      val.isActive = false
      el.style.display = 'none'
    }
    // Fixes responsiveness issue with schedule tables
    if (el.title === 'schedule') {
      const videoSpans = el.querySelectorAll('span.fr-video')
      videoSpans.forEach(span => {
        span.classList.add('schedule-table-responsive')
      })
    }
  })
}

function applyFadeIn(tabTitle: string, tabs: ReadonlyArray<TabComponent>): ReadonlyArray<TabComponent> {
  return tabs.map((val, index) => {
    const el = val.elementRef.nativeElement
    if (val.title === tabTitle || (tabTitle === 'firstTab' && index === 0)) {
      val.renderer.addClass(el.firstChild, 'fadeIn')
    } else {
      val.renderer.removeClass(el.firstChild, 'fadeIn')
    }
    return val
  })
}

@Component({
  selector: 'flo-live-tabs',
  templateUrl: './tabs.component.html',
  styleUrls: ['./tabs.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TabsComponent implements OnChanges {
  @Input()
  get eventAnalytics(): any {
    return {
      ...this._eventAnalytics
    }
  }

  set eventAnalytics(val: any) {
    this._eventAnalytics = val
  }

  constructor(public renderer: Renderer2) {}
  private _eventAnalytics: any
  @ContentChildren(TabComponent)
  readonly tabList: QueryList<TabComponent>
  @Output()
  readonly tabSelected = new EventEmitter<string>()
  @Input()
  readonly initialTab: string
  @Input()
  readonly showStreamTile: boolean
  tabList$: Observable<readonly TabComponent[]>
  readonly currentTabSource = new Subject<string>()

  selectTab(tabTitle: string) {
    this.tabSelected.next(tabTitle)
  }

  trackByTab: TrackByFunction<ITab> = (_index: number, item: ITab) => {
    return item.title
  }

  updateTabSourceWithLatestChange(changes: SimpleChanges) {
    maybe(changes.initialTab).flatMap(maybeMapByExtractingCurrentValue).tapSome(updateTabSource(this.currentTabSource))
  }

  ngOnChanges(changes: SimpleChanges) {
    this.updateTabSourceWithLatestChange(changes)

    this.tabList$ = this.currentTabSource.pipe(
      startWith(this.initialTab),
      flatMap((tabTitle, i) =>
        this.tabList.changes.pipe(
          startWith(this.tabList),
          map(tabs => toTabArray(tabs, this.eventAnalytics, this.showStreamTile)),
          tap(tabs => applyStyles(tabTitle, tabs)),
          map(tabs => {
            /**
             * Do not apply fade animation on first emission to prevent tabs flickering
             */
            if (i === 0) return tabs
            return applyFadeIn(tabTitle, tabs)
          })
        )
      )
    )
  }
}
